import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { Button } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchShipStationConfigs,
  selectShipStationConfig,
  selectShipStationConfigStatus,
  updateShipStationConfigs,
} from '../../../Settings/settingsSlice'
import MyInput from '../../../../features/forms/MyInput'
import MyLabel from '../../../../features/forms/MyLabel'
import MySelect from '../../../../features/forms/MySelect'
import { closeConnectionModal } from '../../integrationsSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { shipstationStoresUrl } from 'urls'
import axios from 'axios'
import { getAxiosDefaultConfig } from 'utils'

export default function ShipstationConnectionModalContent({ retrieveAccounts }) {
  const dispatch = useDispatch()
  const closeModal = () => dispatch(closeConnectionModal())
  const [loadModal, setLoadModal] = useState(false)
  const [loadingShipstation, setLoadingShipstation] = useState(null)
  const [shipstationError, setShipStationError] = useState(false)
  const [noStoresError, setNoStoresError] = useState(false)
  const [stores, setStores] = useState([])

  /**
   *
   * Handle ShipStation config
   *
   */
  const shipStationConfigStatus = useSelector(selectShipStationConfigStatus)
  const shipStationConfig = useSelector(selectShipStationConfig)
  useEffect(() => {
    if (shipStationConfigStatus === 'idle') {
      dispatch(fetchShipStationConfigs())
    }

    if (shipStationConfigStatus === 'loading') {
      setLoadModal(true)
    }

    if (shipStationConfigStatus === 'succeeded') {
      setLoadModal(false)
    }
  }, [shipStationConfigStatus])

  /**
   *
   * Handle form
   *
   */
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    getValues,
    watch,
  } = useForm()

  const fetchStoreIds = async (index) => {
    setLoadingShipstation(index)
    setShipStationError(false)
    setNoStoresError(false)

    try {
      const key = getValues(`${index}.shipstation_client_key`)
      const secret = getValues(`${index}.shipstation_client_secret`)

      const response = await axios.post(
        shipstationStoresUrl,
        { shipStationKey: key, shipStationSecret: secret },
        getAxiosDefaultConfig()
      )

      if (response.data && Array.isArray(response.data)) {
        if (response.data.length > 0) {
          const storeOptions = response.data.map((store) => ({
            label: `${store.storeId} - ${store.storeName}`,
            value: store.storeId,
          }))

          // Update stores state
          setStores((prev) => {
            const newStores = [...prev]
            newStores[index] = storeOptions
            return newStores
          })

          // Clear the current store ID selection when new stores are fetched
          setValue(`${index}.shipstation_store_id`, '')
        } else {
          setNoStoresError(true)
        }
      } else {
        setShipStationError(true)
      }
    } catch (error) {
      console.error('Error fetching stores:', error)
      setShipStationError(true)
    } finally {
      setLoadingShipstation(null)
    }
  }

  // Set default form values
  useEffect(() => {
    if (_.isEmpty(shipStationConfig) && shipStationConfigStatus === 'succeeded') {
      setNoStoresError(true)
      return
    } else setNoStoresError(false)

    if (shipStationConfig.length === 0) return

    const fieldsByStore = shipStationConfig.map(
      (
        {
          channel,
          merchant_id,
          shipstation_client_key,
          shipstation_client_secret,
          shipstation_store_id,
          shipstation_stores,
        },
        i
      ) => {
        return [
          { name: `${i}.channel`, value: channel },
          { name: `${i}.merchant_id`, value: merchant_id },
          { name: `${i}.shipstation_client_key`, value: shipstation_client_key },
          { name: `${i}.shipstation_client_secret`, value: shipstation_client_secret },
          { name: `${i}.shipstation_store_id_default_value`, value: shipstation_store_id },
        ]
      }
    )

    if (fieldsByStore.length) {
      fieldsByStore?.forEach((storeFields) =>
        storeFields.forEach(({ name, value }) => {
          setValue(name, value, { shouldDirty: true })
        })
      )
    }
  }, [shipStationConfig, shipStationConfigStatus])

  const onSubmit = async (values) => {
    const payloadValues = Object.keys(values).map((value) => ({ ...values[value] }))
    const payload = _.values(payloadValues)
    const result = await dispatch(updateShipStationConfigs(payload))
    if (result.meta.requestStatus === 'fulfilled') {
      await retrieveAccounts()
      setTimeout(() => dispatch(closeConnectionModal()), 1000)
    }
    dispatch(fetchShipStationConfigs())
  }

  const isDev = import.meta.env.DEV || window.location.hostname === 'localhost'

  return (
    <div className="tw-p-6">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
        <h2 className="tw-text-xl tw-m-0">Connect Your ShipStation Account</h2>
        {isDev && (
          <span className="tw-bg-yellow-400 tw-text-black tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-font-semibold">
            DEV MODE
          </span>
        )}
      </div>

      {isDev && (
        <div className="tw-mb-4 tw-p-3 tw-bg-yellow-50 tw-border tw-border-yellow-400 tw-rounded">
          <p className="tw-text-sm tw-mb-2 tw-font-semibold">🛠️ Dev Mode Testing:</p>
          <ul className="tw-text-sm tw-mb-0 tw-pl-4">
            <li>Enter any Client Key and Secret (e.g., "test-key" and "test-secret")</li>
            <li>Click "Get Store IDs" to fetch fake test stores</li>
            <li>Select a store ID and save to test the integration flow</li>
          </ul>
        </div>
      )}

      {loadModal ? (
        <FontAwesomeIcon icon={faSpinner} spin />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          {shipStationConfig && shipStationConfig.length > 0 && (
            <div>
              {shipStationConfig.map((storeConfig, i) => {
                const { merchant_id, shipstation_store_id, shipstation_store_name } = storeConfig

                // Watch the specific fields for this store to enable/disable the button
                const clientKey = watch(`${i}.shipstation_client_key`) || ''
                const clientSecret = watch(`${i}.shipstation_client_secret`) || ''
                const isDisabled = !clientKey || !clientSecret ||
                                  clientKey.trim() === '' || clientSecret.trim() === ''

                return (
                  <div
                    key={i}
                    className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-4 tw-mb-6"
                  >
                    <MyInput name={`${i}.channel`} register={register} type="hidden" />

                    <div className="tw-col-span-1">
                      <MyInput
                        register={register}
                        name={`${i}.merchant_id`}
                        label="Store"
                        errors={errors}
                        disabled={true}
                      />
                    </div>
                    <div className="tw-col-span-1">
                      <MyInput
                        register={register}
                        name={`${i}.shipstation_client_key`}
                        label="Client Key"
                        errors={errors}
                        disabled={!merchant_id}
                      />
                    </div>
                    <div className="tw-col-span-1">
                      <MyInput
                        register={register}
                        name={`${i}.shipstation_client_secret`}
                        label="Client Secret"
                        type="password"
                        errors={errors}
                        disabled={!merchant_id}
                      />
                    </div>
                    <div className="tw-col-span-1">
                      <MySelect
                        register={register}
                        name={`${i}.shipstation_store_id`}
                        label="Store ID"
                        errors={errors}
                        options={
                          // Use fetched stores if available, otherwise use existing config stores
                          stores[i] && stores[i].length > 0
                            ? stores[i]
                            : storeConfig.shipstation_stores?.map((store) => ({
                                label: `${store.storeId} - ${store.storeName}`,
                                value: store.storeId,
                              })) || []
                        }
                        defaultValue={
                          shipstation_store_id && shipstation_store_name
                            ? {
                                value: shipstation_store_id,
                                label: `${shipstation_store_id} - ${shipstation_store_name}`,
                                name: `${shipstation_store_id} - ${shipstation_store_name}`, // Add name for compatibility
                              }
                            : {}
                        }
                        theme="white"
                        placeholder="Select a store..."
                        key={`store-select-${i}-${stores[i]?.length || storeConfig.shipstation_stores?.length || 0}`}
                      />
                    </div>
                    <div
                      className="tw-col-span-1"
                      style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}
                    >
                      <Button
                        type="button"
                        disabled={isDisabled || loadingShipstation !== null}
                        onClick={() => fetchStoreIds(i)}
                      >
                        {loadingShipstation === i ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Get Store IDs'}
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {shipstationError && (
            <div>
              <MyLabel text={'Incorrect credentials. Please try again.'} style={{ color: 'red' }} />
            </div>
          )}

          {noStoresError && (
            <div>
              <MyLabel text={'No stores found.'} style={{ color: 'red' }} />
            </div>
          )}

          <div className="tw-flex tw-items-center tw-justify-end tw-space-x-2">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Save Shipstation Connection
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
